import {
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Chip,
} from '@mui/material';

interface BillMetadata {
  provider?: string;
  bill_type?: string;
  amount_due?: number;
  due_date?: string;
  bill_date?: string;
  account_number?: string;
  billing_period?: {
    start?: string;
    end?: string;
  };
  property_address?: string;
  user?: {
    first_name?: string;
    last_name?: string;
    email?: string | null;
    phone?: string | null;
  };
  payment_status?: string;
  autopay_enabled?: boolean;
  electric_details?: {
    total: number;
    supply: number;
    delivery: number;
    ev_credit?: number;
  };
  gas_details?: {
    total: number;
    supply: number;
    delivery: number;
  };
  total_usage?: {
    gas_therms?: number;
    electric_kwh?: number;
  };
}

interface ExtractedDocumentData {
  success: boolean;
  summary?: string;
  document_type?: string;
  custom_metadata?: BillMetadata;
  id?: number;
  ocred?: boolean;
  checksum?: string;
  file_url?: string;
  data_type?: string;
  user_uuid?: string | null;
  created_at?: string;
  updated_at?: string;
  website_id?: number | null;
}

interface DocumentExtractPanelProps {
  data: ExtractedDocumentData | undefined;
  isLoading: boolean;
  error: Error | undefined;
}

/**
 * Component to display utility bill extraction data
 */
const DocumentExtractPanel: React.FC<DocumentExtractPanelProps> = ({ data, isLoading, error }) => {
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 3,
        }}
      >
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Analyzing document...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Error analyzing document
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error.message || 'An unexpected error occurred'}
        </Typography>
      </Box>
    );
  }

  if (!data?.custom_metadata) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="text.secondary">
          No information available for this document.
        </Typography>
      </Box>
    );
  }

  const { custom_metadata: bill } = data;

  // Format date function
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // Format currency function
  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return 'N/A';
    return `$${amount.toFixed(2)}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Utility Bill Details</Typography>
        {bill.bill_type && (
          <Chip
            label={bill.bill_type
              .split('_')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')}
            color="primary"
            sx={{ textTransform: 'capitalize' }}
          />
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />

      {/* Summary Section */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="primary">
            Bill Summary
          </Typography>
          <Typography variant="body1">
            <strong>Provider:</strong> {bill.provider || 'N/A'}
          </Typography>
          <Box
            sx={{ mt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="h4" color="error">
              {formatCurrency(bill.amount_due)}
            </Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Due Date: {formatDate(bill.due_date)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Bill Date: {formatDate(bill.bill_date)}
              </Typography>
              {bill.payment_status && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Status:{' '}
                  <Chip
                    size="small"
                    label={bill.autopay_enabled ? 'Autopay Enabled' : bill.payment_status}
                    color="success"
                  />
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Account Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Account Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Account Number:</strong> {bill.account_number || 'N/A'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Billing Period:</strong> {formatDate(bill.billing_period?.start)} -{' '}
                {formatDate(bill.billing_period?.end)}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Customer:</strong> {bill.user?.first_name || ''}{' '}
                {bill.user?.last_name || ''}
              </Typography>
              <Typography variant="body2">
                <strong>Property Address:</strong> {bill.property_address || 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Usage Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom color="primary">
                Usage Information
              </Typography>
              {bill.total_usage?.electric_kwh && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Electricity Usage:</strong> {bill.total_usage.electric_kwh} kWh
                </Typography>
              )}
              {bill.total_usage?.gas_therms && (
                <Typography variant="body2">
                  <strong>Gas Usage:</strong> {bill.total_usage.gas_therms} therms
                </Typography>
              )}
              {!bill.total_usage?.electric_kwh && !bill.total_usage?.gas_therms && (
                <Typography variant="body2">No usage information available</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Electric Details */}
        {bill.electric_details && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Electricity Charges
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Delivery Charges:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(bill.electric_details.delivery)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Supply Charges:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(bill.electric_details.supply)}
                  </Typography>
                </Box>
                {bill.electric_details.ev_credit && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">EV Credit:</Typography>
                    <Typography variant="body2" color="success.main">
                      -{formatCurrency(bill.electric_details.ev_credit)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">
                    <strong>Total Electric:</strong>
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>{formatCurrency(bill.electric_details.total)}</strong>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Gas Details */}
        {bill.gas_details && (
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Gas Charges
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Delivery Charges:</Typography>
                  <Typography variant="body2">
                    {formatCurrency(bill.gas_details.delivery)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Supply Charges:</Typography>
                  <Typography variant="body2">{formatCurrency(bill.gas_details.supply)}</Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1">
                    <strong>Total Gas:</strong>
                  </Typography>
                  <Typography variant="subtitle1">
                    <strong>{formatCurrency(bill.gas_details.total)}</strong>
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default DocumentExtractPanel;
